import {
  API_VERSION_DEFAULTS,
  BD_FLEET_HEADER_NAME,
  CONTINUATION_TOKEN_HEADER_NAME,
  DEFAULT_API_CONFIG,
  PERIPHERALS_API_CONFIG,
} from '~/common/apis/api.constants';
import type { RouteParams } from '~/common/configs/route.config';
import type { ApiResponse } from '~/common/models/apis/apiResponse.model';
import type {
  FormPayload,
  PagedResult,
  PagedResultWithErrors,
} from '~/common/models/common.model';
import { BDAppErrorType, BDError } from '~/common/models/error.model';
import type {
  ApiPeripheralMoveFleetSummary,
  PeripheralListItem,
  PeripheralOnboardStatusApiResponse,
  PeripheralsOnboardApiResponse,
  ValidPeripheralOnboardForm,
} from '~/common/models/peripheral.model';
import {
  addAcceptLanguageHeader,
  addHeader,
  addOrganizationIdHeader,
  hasApiResult,
} from '~/common/utils/apis/api.utils';
import {
  makeGetPayloadCreator,
  makePostPayloadCreator,
  makeThunk,
} from '~/common/utils/store/thunk.helper';

import {
  DEFAULT_PERIPHERAL_TYPE,
  ERROR_MSG_GET_PERIPHERALS_ITEMS,
  ERROR_MSG_MAPPING_PERIPHERALS,
  ERROR_MSG_UNEXPECTED_ONBOARD_RESPONSE,
  ERROR_MSG_UNEXPECTED_RESPONSE,
} from '../config/constants/core';
import { buildPeripheralRequestParams } from '../utils/peripheralsQuery.utils';
import { PeripheralsSessionViewType } from './peripheralsSlice.model';

export enum PeripheralsActionType {
  GET_PERIPHERALS = 'peripherals/getPeripherals',
  ONBOARD_PERIPHERALS = 'peripherals/postPeripheralsOnboard',
  ONBOARD_STATUS_PERIPHERALS = 'peripherals/getOnboardStatus',
  POST_LINK_TO_VEHICLE = 'peripherals/postLinkToVehicle',
  POST_UNLINK_TO_VEHICLE = 'peripherals/postUnlinkToVehicle',
  POST_MOVE_FLEET = 'peripherals/postMoveFleet',
}

export type GetPeripheralsParams = {
  sessionId: string;
  pageIndex?: number;
  size?: number;
  continuationToken?: string | null;
  fleetId?: string;
  organizationId?: string;
};

const getPeripherals = makeThunk(
  PeripheralsActionType.GET_PERIPHERALS,
  makeGetPayloadCreator<
    ApiResponse<PagedResultWithErrors<PeripheralListItem>>,
    GetPeripheralsParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/peripherals/${API_VERSION_DEFAULTS.peripheralsOnboardingAPI}`,
    axiosOptions: ({ continuationToken, pageIndex }, state) => {
      const commonHeaders = addAcceptLanguageHeader(
        DEFAULT_API_CONFIG,
        state.profile.currentLocale
      );
      return continuationToken && pageIndex && pageIndex > 0
        ? addHeader(
            commonHeaders,
            CONTINUATION_TOKEN_HEADER_NAME,
            continuationToken
          )
        : commonHeaders;
    },
    argAdapter: (args, state) => {
      const { sessionId, fleetId, size, organizationId } = args;
      const session =
        state.peripherals.sessionConfigs[
          PeripheralsSessionViewType.LIST_VIEW
        ]?.[sessionId];
      const { searchCriteria, filterType, peripheralType } = session || {};
      const finalPeripheralType = peripheralType || DEFAULT_PERIPHERAL_TYPE;
      const requestParams = buildPeripheralRequestParams(
        finalPeripheralType,
        searchCriteria,
        filterType,
        size,
        fleetId
      );
      if (organizationId) {
        requestParams.organizationId = organizationId;
      }
      return { requestParams };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<PagedResult<PeripheralListItem>>(response)) {
        const errors: BDError[] = [];
        const { items, total_items, continuation_token } = response.result;
        const mappedItems =
          items?.reduce((acc: PeripheralListItem[], apiPeripheral) => {
            try {
              acc.push(apiPeripheral);
            } catch (e) {
              errors.push({
                name: ERROR_MSG_GET_PERIPHERALS_ITEMS,
                type: BDAppErrorType.VALIDATION,
                message:
                  e instanceof Error
                    ? e.message
                    : ERROR_MSG_MAPPING_PERIPHERALS,
                data: apiPeripheral,
              });
            }
            return acc;
          }, []) || [];
        const finalResult = {
          ...response,
          result: {
            total_items,
            continuation_token,
            items: mappedItems,
          },
        };
        return errors.length
          ? { ...finalResult, errors: errors.map((e) => BDError.asJson(e)) }
          : finalResult;
      }
      throw new BDError(ERROR_MSG_UNEXPECTED_RESPONSE, { data: response });
    },
  })
);

export type PeripheralsOnboardStatusParams = {
  id: string;
  organizationId: string;
  /** Session key for storing status (e.g. onboard-${peripheralType}). Enables separate state per flow. */
  sessionKey?: string;
};

export type PostPeripheralsOnboardArg = FormPayload<
  ValidPeripheralOnboardForm,
  'organizationsId'
> & { onboardSessionKey?: string };

/**
 * Single onboard API for all peripheral types (cameras, adapters, etc.).
 * The `type` query param (from formFields.peripheralType) distinguishes which kind is being onboarded.
 */
const postPeripheralsOnboard = makeThunk(
  PeripheralsActionType.ONBOARD_PERIPHERALS,
  makePostPayloadCreator<
    ApiResponse<PeripheralsOnboardApiResponse>,
    PostPeripheralsOnboardArg
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/peripherals/${API_VERSION_DEFAULTS.peripheralsOnboardingAPI}/onboard`,
    axiosOptions: (arg: PostPeripheralsOnboardArg, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(
          PERIPHERALS_API_CONFIG,
          arg.formFields.organizationsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: ({ formFields }: PostPeripheralsOnboardArg) => {
      if (!formFields?.csvFile || !(formFields.csvFile instanceof File)) {
        throw new BDError(ERROR_MSG_UNEXPECTED_ONBOARD_RESPONSE, {
          data: { message: 'CSV file is required for onboard' },
        });
      }
      const formData = new FormData();
      formData.append(
        'file',
        formFields.csvFile,
        formFields.csvFile.name || 'data.csv'
      );
      return {
        requestBody: formData,
        requestParams: {
          organizationId: formFields.organizationsId,
          type: formFields.peripheralType,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<PeripheralsOnboardApiResponse>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError(ERROR_MSG_UNEXPECTED_ONBOARD_RESPONSE, {
        data: response,
      });
    },
  })
);

const CONFIG = DEFAULT_API_CONFIG;

const getPeripheralsOnboardStatus = makeThunk(
  PeripheralsActionType.ONBOARD_STATUS_PERIPHERALS,
  makeGetPayloadCreator<
    ApiResponse<PeripheralOnboardStatusApiResponse>,
    PeripheralsOnboardStatusParams
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/peripherals/${API_VERSION_DEFAULTS.peripheralsOnboardingAPI}/batch-statuses/:id`,
    axiosOptions: ({ organizationId }, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(CONFIG, organizationId),
        state.profile.currentLocale
      ),
    argAdapter: ({ id }) => {
      return {
        requestParams: {
          id,
        },
      };
    },
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (
        response &&
        hasApiResult<PeripheralOnboardStatusApiResponse>(response)
      ) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError(ERROR_MSG_UNEXPECTED_ONBOARD_RESPONSE, {
        data: response,
      });
    },
  })
);

type LinkParams = Pick<RouteParams, 'organizationsId' | 'fleetsId'> & {
  id: string;
  vin: string;
};

const postLinkVehicle = makeThunk(
  PeripheralsActionType.POST_LINK_TO_VEHICLE,
  makePostPayloadCreator<ApiResponse, LinkParams>({
    url: ({ id }) =>
      `${globalThis.appConfig.apiBaseUrl}/peripherals/${API_VERSION_DEFAULTS.peripheralsAPI}/${id}/link-vehicle`,
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(
          addHeader(CONFIG, BD_FLEET_HEADER_NAME, fleetsId),
          organizationsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: ({ vin }) => {
      return {
        requestBody: {
          vin,
        },
      };
    },
  })
);

type UnlinkParams = Pick<RouteParams, 'organizationsId' | 'fleetsId'> & {
  id: string;
  sessionId: string;
};

const postUnlinkVehicle = makeThunk(
  PeripheralsActionType.POST_UNLINK_TO_VEHICLE,
  makePostPayloadCreator<ApiResponse, UnlinkParams>({
    url: `${globalThis.appConfig.apiBaseUrl}/peripherals/${API_VERSION_DEFAULTS.peripheralsUnlinkAPI}/:id/unlink-vehicle`,
    axiosOptions: ({ organizationsId, fleetsId }, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(
          addHeader(CONFIG, BD_FLEET_HEADER_NAME, fleetsId),
          organizationsId
        ),
        state.profile.currentLocale
      ),
    argAdapter: ({ id }) => {
      return {
        requestParams: {
          id,
        },
      };
    },
  })
);

type MoveFleetPayload = Pick<RouteParams, 'organizationsId'> & {
  peripherals: PeripheralListItem[];
  sessionId: string;
  fleetId?: string;
};

export const postMoveFleet = makeThunk(
  PeripheralsActionType.POST_MOVE_FLEET,
  makePostPayloadCreator<
    ApiResponse<ApiPeripheralMoveFleetSummary>,
    MoveFleetPayload
  >({
    url: `${globalThis.appConfig.apiBaseUrl}/peripherals/${API_VERSION_DEFAULTS.peripheralsMoveAPI}/move-fleet`,
    axiosOptions: ({ organizationsId }, state) =>
      addAcceptLanguageHeader(
        addOrganizationIdHeader(CONFIG, organizationsId),
        state.profile.currentLocale
      ),
    argAdapter: ({ peripherals, fleetId }) => ({
      requestBody: {
        destination: {
          fleetId,
        },
        peripheralIds: peripherals.map((peripheral) => peripheral.id),
      },
    }),
    responseAdapter: (response: unknown | ApiResponse<unknown>) => {
      if (response && hasApiResult<ApiPeripheralMoveFleetSummary>(response)) {
        return {
          ...response,
          result: response.result,
        };
      }
      throw new BDError('Unexpected Move Fleet response', {
        data: response,
      });
    },
  })
);
export const PERIPHERALS_ACTIONS = {
  getPeripherals,
  postPeripheralsOnboard,
  getPeripheralsOnboardStatus,
  postLinkVehicle,
  postUnlinkVehicle,
  postMoveFleet,
};
